import { useState } from 'react';
import { Button, Flex } from '../ui';
import { Pencil } from 'lucide-react';
import { InputModal } from '../modals';
import DealerTag from '../ui/DealerTag';

import type { Round, InputValue, ModalOpenArgs } from '../../types';
import { isActualAutoGenerated } from '../../helpers/utils/helperFunctions';

interface PlayerInputProps {
  inputId: string;
  dealerId: string;
  type: 'Bid' | 'Actual';
  index: number;
  isCurrent: boolean;
  playerName: string;
  playerInput: InputValue;
  roundHistory: Round[];
  currentRound: Round;
  teamClassName: string;
  fieldToUpdate: string;
  onOpenModal?: (args: ModalOpenArgs) => void;
  teamName?: string; // allow it but we might not use it
}

const PlayerInput = ({
  inputId,
  dealerId,
  type,
  index,
  isCurrent,
  playerName,
  playerInput,
  roundHistory,
  currentRound,
  teamClassName,
  fieldToUpdate,
  onOpenModal,
}: PlayerInputProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onEdit = () => {
    if (onOpenModal) {
      onOpenModal({
        fieldToUpdate,
        type,
        playerName,
        inputId,
        dealerId,
      });
    } else {
      setIsModalOpen(true);
    }
  };

  // Always show the actual value, with asterisk if auto-generated
  const displayValue =
    playerInput === ''
      ? ''
      : `${playerInput}${isActualAutoGenerated(fieldToUpdate, currentRound, type) ? '*' : ''}`;
  const showButton = displayValue === '';

  return (
    <>
      {!onOpenModal && (
        <InputModal
          isCurrent={isCurrent}
          playerName={playerName}
          isOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          type={type}
          fieldToUpdate={fieldToUpdate}
          currentRound={currentRound}
          roundHistory={roundHistory}
          index={index}
        />
      )}
      <Flex my={'5px'} direction={'row'} justify={'flex-start'} align="center">
        <Flex direction={'row'} align={'center'} flex={1} minW={0} mr={1}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {playerName}
          </span>
          {type === 'Bid' && (
            <DealerTag
              id={dealerId}
              index={index}
              isCurrent={isCurrent}
              roundHistory={roundHistory}
            />
          )}
        </Flex>
        {showButton ? (
          <Button
            onClick={onEdit}
            value={displayValue}
            id={inputId}
            name={inputId}
            size="xs"
            variant={
              teamClassName === 'team1' ? 'team1Outline' : 'team2Outline'
            }
            data-cy={type === 'Bid' ? 'bidButton' : 'actualButton'}
            data-testid={type === 'Bid' ? 'bidButton' : 'actualButton'}
            mr={teamClassName === 'team1' ? 1 : 0}
          >
            {displayValue || type}
          </Button>
        ) : (
          <div data-cy="playerInput" data-testid="playerInput">
            <Flex
              align="center"
              justify="center"
              cursor="pointer"
              onClick={onEdit}
              mr={teamClassName === 'team1' ? 1 : 0}
            >
              <Flex
                borderColor={teamClassName === 'team1' ? 'team1' : 'team2'}
                borderRadius="var(--app-radius-sm)"
                px="0.5rem"
              >
                {displayValue}
              </Flex>
              <Pencil
                color={
                  teamClassName === 'team1'
                    ? 'var(--chakra-colors-team1)'
                    : 'var(--chakra-colors-team2)'
                }
                size={20}
                style={{ marginLeft: '5px' }}
                data-testid="editIcon"
              />
            </Flex>
          </div>
        )}
      </Flex>
    </>
  );
};

export default PlayerInput;
